import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { motion } from "framer-motion";
import { Coins, Heart, Sparkles, Star, Crown, Info, ShieldCheck, Gift, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

import { supabase } from "@/integrations/supabase/client";
import { RouteError, RouteNotFound } from "@/components/game/RouteError";
import { formatEuro, formatGold, findGoldPackById, type GoldPack } from "@/lib/payments/gold-packs";
import { usePaddleCheckout } from "@/hooks/usePaddleCheckout";
import { getMyMonster } from "@/lib/game/monster.functions";
import { ThankYouOverlay } from "@/components/game/ThankYouOverlay";
import { PurchaseConfirmDialog } from "@/components/PurchaseConfirmDialog";
import { Panel } from "@/components/game/Panel";
import { Pill } from "@/components/game/Badges";
import { GoldAmount, MicroLabel, PageHeader, ScreenContainer } from "@/components/game/ScreenKit";

import {
  CYCLE_TARGET_GOLD,
  getGoldPackOffers,
  prepareGoldPackCheckout,
  type GoldPackOffer,
} from "@/lib/payments/upgrade.functions";
import { getPaddleEnvironment } from "@/lib/paddle";
import { useTutorial } from "@/components/game/TutorialProvider";
import { tServerError } from "@/lib/i18n/server-error";

export const Route = createFileRoute("/_app/game/gold-shop")({
  component: GoldShop,
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  validateSearch: (search: Record<string, unknown>): { checkout?: string } => ({
    checkout: typeof search.checkout === "string" ? search.checkout : undefined,
  }),
});

/**
 * Zyklus-Balken. Bewusst KEIN StatBar: der zeigt „Wert/Maximum" als nackte
 * Zahlen an, hier sind das Cent-Beträge — „500/6000" statt „5,00 € von 60,00 €".
 * Geometrie (Ink-Rinne, 2-px-Outline, zweifarbige Füllung) ist von StatBar
 * übernommen, die Füllung liegt in `accent`: der Balken misst Euro, und Gelb
 * ist im Design für Gold-Beträge reserviert.
 */
function CycleBar({ pct }: { pct: number }) {
  return (
    <div className="relative box-border h-3.5 overflow-hidden rounded-full border-2 border-ink bg-ink">
      <div
        className="h-full rounded-full"
        style={{
          width: `${Math.max(0, Math.min(100, pct))}%`,
          background: "linear-gradient(180deg, oklch(0.72 0.13 262) 0 45%, var(--accent) 45%)",
          transition: "width .35s cubic-bezier(.34,1.3,.64,1)",
        }}
      />
    </div>
  );
}

function GoldShop() {
  const { t } = useTranslation("game");
  useTutorial("gold_shop_intro");
  const { openCheckout, loading } = usePaddleCheckout();
  const fetchMon = useServerFn(getMyMonster);
  const fetchOffers = useServerFn(getGoldPackOffers);
  const prepareCheckout = useServerFn(prepareGoldPackCheckout);
  const queryClient = useQueryClient();
  const monQ = useQuery({ queryKey: ["my-monster"], queryFn: () => fetchMon(), staleTime: 30_000 });
  const monster = monQ.data?.monster;

  const offersQ = useQuery({
    queryKey: ["gold-pack-offers"],
    queryFn: () => fetchOffers(),
    staleTime: 10_000,
    refetchOnWindowFocus: true,
  });
  const cycleSpentCents = offersQ.data?.cycleSpentCents ?? 0;
  // Fallback = die echte Konstante des Servers. Vorher stand hier 300_000 —
  // eine Zahl, die es nirgends gibt; sie blitzte bis zum Eintreffen der Daten auf.
  const targetGold = offersQ.data?.targetGold ?? CYCLE_TARGET_GOLD;
  const largestPackCents = offersQ.data?.largestPackCents ?? 6000;
  const cycleRemainingCents = offersQ.data?.cycleRemainingCents ?? largestPackCents;

  const offers = offersQ.data?.offers ?? [];

  const search = Route.useSearch() as { checkout?: string };
  const navigate = useNavigate();
  const [thanksOpen, setThanksOpen] = useState(false);
  const [lastPurchase, setLastPurchase] = useState<{
    packLabel: string;
    goldAmount: number;
  } | null>(null);
  const [confirmPack, setConfirmPack] = useState<{
    pack: GoldPack;
    offer: GoldPackOffer;
  } | null>(null);
  const handledSuccess = useRef(false);
  const expectedBonusRef = useRef<Map<string, number>>(new Map());

  const pollOffers = async () => {
    const cached = queryClient.getQueryData<{
      cycleSpentCents?: number;
      paymentsCount?: number;
      cycleGoldEarned?: number;
    }>(["gold-pack-offers"]);
    const startSpent = cached?.cycleSpentCents ?? 0;
    const startCount = cached?.paymentsCount ?? 0;
    const startEarned = cached?.cycleGoldEarned ?? 0;
    for (let i = 0; i < 15; i++) {
      await new Promise((r) => setTimeout(r, 2000));
      const [ctx] = await Promise.all([
        queryClient.fetchQuery({
          queryKey: ["gold-pack-offers"],
          queryFn: () => fetchOffers(),
          staleTime: 0,
        }),
        queryClient.invalidateQueries({ queryKey: ["my-monster"] }),
      ]);
      const nowSpent = ctx?.cycleSpentCents ?? 0;
      const nowCount = ctx?.paymentsCount ?? 0;
      const nowEarned = ctx?.cycleGoldEarned ?? 0;
      if (nowSpent !== startSpent || nowCount !== startCount || nowEarned !== startEarned) {
        await queryClient.invalidateQueries({ queryKey: ["gold-pack-offers"] });
        await queryClient.invalidateQueries({ queryKey: ["my-monster"] });
        return;
      }
    }
    await queryClient.invalidateQueries({ queryKey: ["gold-pack-offers"] });
    await queryClient.invalidateQueries({ queryKey: ["my-monster"] });
  };

  const pollOffersRef = useRef(pollOffers);
  pollOffersRef.current = pollOffers;
  const openThanksRef = useRef<() => void>(() => setThanksOpen(true));
  openThanksRef.current = () => setThanksOpen(true);

  useEffect(() => {
    if (search?.checkout !== "success" || handledSuccess.current) return;
    handledSuccess.current = true;
    setThanksOpen(true);
    navigate({ to: "/game/gold-shop", search: {}, replace: true });
    void pollOffers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search?.checkout]);

  useEffect(() => {
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let cancelled = false;

    const refresh = () => {
      queryClient.invalidateQueries({ queryKey: ["gold-pack-offers"] });
      queryClient.invalidateQueries({ queryKey: ["my-monster"] });
    };

    (async () => {
      const { data } = await supabase.auth.getSession();
      const userId = data.session?.user?.id ?? null;
      if (!userId || cancelled) return;
      channel = supabase
        .channel(`gold-shop-${userId}`)
        .on("postgres_changes", { event: "*", schema: "public", table: "profiles", filter: `id=eq.${userId}` }, refresh)
        .on("postgres_changes", { event: "INSERT", schema: "public", table: "gold_purchases", filter: `user_id=eq.${userId}` },
          (payload: any) => {
            const row = payload?.new ?? {};
            const pack = row.pack_id ? findGoldPackById(row.pack_id) : null;
            if (pack) {
              const baseGold = Number(row.gold_amount ?? pack.gold);
              const bonus = expectedBonusRef.current.get(pack.id) ?? 0;
              expectedBonusRef.current.delete(pack.id);
              setLastPurchase({ packLabel: pack.label, goldAmount: baseGold + bonus });
            }
            openThanksRef.current();
            refresh();
          },
        )
        .on("postgres_changes", { event: "*", schema: "public", table: "gold_cycle_payments", filter: `user_id=eq.${userId}` }, refresh)
        .subscribe();
    })();

    return () => {
      cancelled = true;
      if (channel) supabase.removeChannel(channel);
    };
  }, [queryClient]);

  const handleBuy = (pack: GoldPack) => {
    const offer = offers.find((o) => o.packId === pack.id);
    if (!offer || !offer.visible) {
      toast.error(t("goldShop.packUnavailable"));
      return;
    }
    setConfirmPack({ pack, offer });
  };

  const executeBuy = async () => {
    if (!confirmPack) return;
    const { pack, offer } = confirmPack;
    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData.session?.user?.id;
    const email = sessionData.session?.user?.email ?? undefined;
    if (!userId) {
      toast.error(t("goldShop.reloginRequired"));
      return;
    }
    try {
      const prep = await prepareCheckout({ data: { packId: pack.id, environment: getPaddleEnvironment() } });
      setConfirmPack(null);
      const expectedBonus = offer?.bonusGoldOnPurchase ?? 0;
      const expectedGold = pack.gold + expectedBonus;
      expectedBonusRef.current.set(pack.id, expectedBonus);
      await openCheckout({
        priceId: pack.priceId,
        paddlePriceId: prep.paddlePriceId,
        discountId: prep.discountId,
        customerEmail: email,
        customData: { userId, packId: pack.id },
        onCompleted: () => {
          expectedBonusRef.current.delete(pack.id);
          setLastPurchase({ packLabel: pack.label, goldAmount: expectedGold });
          openThanksRef.current();
          void pollOffersRef.current();
        },
      });
    } catch (e) {
      toast.error(tServerError(e));
    }
  };

  const visibleOffers = offers.filter((o) => o.visible);
  const cyclePct = Math.round((cycleSpentCents / Math.max(1, largestPackCents)) * 100);

  return (
    <ScreenContainer>
      <PageHeader
        title={t("goldShop.title")}
        icon={<Coins className="h-[22px] w-[22px] shrink-0 text-gold" strokeWidth={2.5} />}
        actions={
          monster ? (
            /* Gold-Anzeige als Pille auf Ink — liest sich als Wert, nicht als Knopf
               (gleiches Muster wie im Kopf der GameShell). */
            <span className="inline-flex items-center rounded-full border-2 border-ink bg-ink px-3 py-1.5">
              <GoldAmount value={Number(monster.gold ?? 0)} size="md" />
            </span>
          ) : undefined
        }
      />

      <Panel tone="primary" grid contentClassName="p-3.5">
        <div className="flex items-start gap-3">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-md border-2 border-ink bg-secondary">
            <Heart className="h-5 w-5 text-primary" strokeWidth={2.5} />
          </span>
          <div className="min-w-0 space-y-1.5">
            <h3 className="text-[17px] leading-tight [overflow-wrap:anywhere]">
              {t("goldShop.heart.headline")}
            </h3>
            <p className="text-[13px] leading-relaxed text-muted-foreground">
              {t("goldShop.heart.body")}{" "}
              <span className="font-extrabold text-foreground">{t("goldShop.heart.thanks")}</span>
            </p>
          </div>
        </div>
      </Panel>

      <div className="mb-2.5 mt-5 flex items-center gap-2">
        <Sparkles className="h-4 w-4 shrink-0 text-primary" strokeWidth={2.5} />
        <h3 className="text-[17px]">{t("goldShop.packagesHeading")}</h3>
      </div>

      <Panel grid contentClassName="p-3.5">
        <div className="flex items-start gap-3">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-md border-2 border-ink bg-gold text-ink">
            <Gift className="h-5 w-5" strokeWidth={2.5} />
          </span>
          <div className="min-w-0 flex-1 space-y-2">
            <div>
              <MicroLabel>{t("goldShop.fairness.label")}</MicroLabel>
              <p className="mt-1 text-[13px] leading-relaxed">
                {t("goldShop.fairness.description", {
                  max: formatEuro(largestPackCents),
                  gold: formatGold(targetGold),
                })}
              </p>
            </div>
            <p className="text-xs leading-relaxed tabular-nums text-muted-foreground">
              {t("goldShop.fairness.counter", {
                paid: formatEuro(cycleSpentCents),
                max: formatEuro(largestPackCents),
                remaining: formatEuro(Math.max(0, cycleRemainingCents)),
                gold: formatGold(targetGold),
              })}
            </p>
            <CycleBar pct={cyclePct} />
          </div>
        </div>
      </Panel>

      <div className="mt-3.5 grid gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
        {offersQ.isLoading && (
          <p className="col-span-full text-sm text-muted-foreground">{t("goldShop.loadingOffers")}</p>
        )}
        {offersQ.isError && (
          <div className="col-span-full">
            <Panel tone="enemy" contentClassName="p-3.5">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" strokeWidth={2.5} />
                <div className="min-w-0 space-y-2">
                  <h3 className="text-[15px] text-destructive">{t("goldShop.offersError")}</h3>
                  <p className="text-xs leading-relaxed text-muted-foreground [overflow-wrap:anywhere]">
                    {(offersQ.error as Error)?.message ?? t("goldShop.retryHint")}
                  </p>
                  <Button size="sm" variant="outline" onClick={() => offersQ.refetch()}>
                    {t("goldShop.retry")}
                  </Button>
                </div>
              </div>
            </Panel>
          </div>
        )}
        {visibleOffers.map((offer, idx) => {
          const pack = findGoldPackById(offer.packId);
          if (!pack) return null;
          return (
            <PackCard
              key={pack.id}
              pack={pack}
              offer={offer}
              index={idx}
              loading={loading}
              onBuy={() => handleBuy(pack)}
            />
          );
        })}
      </div>

      <Panel tone="secondary" className="mt-7" contentClassName="p-3.5">
        <div className="space-y-2.5 text-xs leading-relaxed text-muted-foreground">
          <div className="flex items-start gap-2">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2.5} />
            <p>{t("goldShop.legal.paymentNote")}</p>
          </div>
          <div className="flex items-start gap-2">
            <Info className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2.5} />
            <p>
              {t("goldShop.legal.goldNote")}{" "}
              <Link to="/widerruf" className="text-primary underline">
                {t("goldShop.legal.guarantee")}
              </Link>{" "}
              {t("goldShop.legal.goldNoteEnd")}{" "}
              <Link to="/agb" className="text-primary underline">{t("goldShop.legal.tos")}</Link>
              {", "}
              <Link to="/datenschutz" className="text-primary underline">{t("goldShop.legal.privacy")}</Link>{" "}
              {t("goldShop.legal.and")}{" "}
              <Link to="/impressum" className="text-primary underline">{t("goldShop.legal.imprint")}</Link>
              {"."}
            </p>
          </div>
        </div>
      </Panel>

      {confirmPack && (
        <PurchaseConfirmDialog
          open={!!confirmPack}
          onClose={() => setConfirmPack(null)}
          onConfirm={executeBuy}
          packLabel={confirmPack.pack.label}
          packGold={confirmPack.pack.gold}
          bonusGold={confirmPack.offer.bonusGoldOnPurchase}
          originalCents={confirmPack.offer.originalCents}
          finalCents={confirmPack.offer.finalCents}
          loading={loading}
        />
      )}

      {monster && (
        <ThankYouOverlay
          open={thanksOpen}
          onClose={() => setThanksOpen(false)}
          monsterName={monster.name}
          imageUrl={monster.image_url ?? null}
          packLabel={lastPurchase?.packLabel ?? null}
          goldAmount={lastPurchase?.goldAmount ?? null}
        />
      )}
    </ScreenContainer>
  );
}

function PackCard({
  pack,
  offer,
  index,
  loading,
  onBuy,
}: {
  pack: GoldPack;
  offer: GoldPackOffer;
  index: number;
  loading: boolean;
  onBuy: () => void;
}) {
  const { t } = useTranslation("game");
  const Highlight =
    pack.highlight === "best-value" ? Crown : pack.highlight === "popular" ? Star : null;

  const { isDiscounted, finalCents, originalCents, wasRoundedUp, bonusGoldOnPurchase, triggersCycleReset } = offer;
  const discountCents = originalCents - finalCents;
  const totalGold = pack.gold + bonusGoldOnPurchase;

  // Der Ton trägt die Bedeutung, nicht die Dekoration: Gold-Outline (`boss`,
  // farbgleich mit `--gold`) für das Paket mit der Fairness-Garantie, Art-Blau
  // (`primary`) für einen persönlichen Aktionspreis, sonst die normale
  // Ink-Outline.
  //
  // ⚠️ KEIN `holo` hier. Der Holo-Schimmer ist im Design-Kanon die Auszeichnung
  // für Legendär und Boss (docs/design-system/readme.md, „Hierarchie-Ebenen";
  // Panel.tsx sagt es am Prop noch einmal). Ein Gold-Paket ist eine Preisstufe,
  // kein seltener Fund — schimmerte es mit, hieße der Schimmer im ganzen Spiel
  // nicht mehr „selten", sondern „teuer". Ausgezeichnet wird das Paket über die
  // Gold-Pille und den Gold-Kasten darunter.
  const tone = triggersCycleReset ? "boss" : isDiscounted ? "primary" : "default";

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="h-full"
    >
      <Panel tone={tone} className="h-full" contentClassName="flex h-full flex-col gap-3 p-3.5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <MicroLabel className="text-foreground">{pack.label}</MicroLabel>
          {triggersCycleReset ? (
            <Pill style={{ background: "var(--gold)", color: "var(--ink)" }}>
              <Gift className="h-3 w-3" strokeWidth={2.5} />
              {t("goldShop.pack.fairnessBoost")}
            </Pill>
          ) : isDiscounted ? (
            <Pill style={{ background: "var(--hp-ally)", color: "var(--ink)" }}>
              {t("goldShop.pack.upgradePrice")}
            </Pill>
          ) : Highlight ? (
            <Pill
              style={
                pack.highlight === "best-value"
                  ? { background: "var(--gold)", color: "var(--ink)" }
                  : { background: "var(--secondary)", color: "var(--foreground)" }
              }
            >
              <Highlight className="h-3 w-3" strokeWidth={2.5} />
              {pack.highlight === "best-value"
                ? t("goldShop.pack.bestValue")
                : t("goldShop.pack.popular")}
            </Pill>
          ) : null}
        </div>

        {bonusGoldOnPurchase > 0 ? (
          <div className="space-y-2">
            <div>
              <MicroLabel>{t("goldShop.pack.packGold")}</MicroLabel>
              <GoldAmount value={pack.gold} size="lg" />
            </div>
            <div className="flex items-center gap-2 rounded-md border-2 border-ink bg-gold px-2.5 py-1.5 text-ink">
              <Gift className="h-4 w-4 shrink-0" strokeWidth={2.5} />
              <span className="font-display text-[13px] leading-tight">
                {t("goldShop.pack.fairnessBoostAmount", { amount: formatGold(bonusGoldOnPurchase) })}
              </span>
            </div>
            <div className="border-t-2 border-border-soft pt-2">
              <MicroLabel>{t("goldShop.pack.totalGold")}</MicroLabel>
              <GoldAmount value={totalGold} size="lg" className="text-2xl" />
            </div>
          </div>
        ) : (
          <div>
            <MicroLabel>{t("goldShop.pack.goldUnit")}</MicroLabel>
            <GoldAmount value={totalGold} size="lg" className="text-2xl" />
            {pack.bonusPct > 0 && (
              /* eigener Block: Pill und Gold-Betrag sind beide inline und
                 säßen sonst nebeneinander in einer Zeile. */
              <div className="mt-2">
                <Pill style={{ background: "var(--secondary)", color: "var(--foreground)" }}>
                  {t("goldShop.pack.bonus", { pct: pack.bonusPct })}
                </Pill>
              </div>
            )}
          </div>
        )}

        <div className="mt-auto">
          <div className="font-display text-2xl leading-none ink-shadow">{formatEuro(finalCents)}</div>
          {isDiscounted && (
            <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
              <span className="tabular-nums text-muted-foreground line-through">
                {formatEuro(originalCents)}
              </span>
              <span className="font-extrabold text-hp-ally">
                {t("goldShop.pack.savings", { amount: formatEuro(discountCents) })}
              </span>
            </div>
          )}
          {wasRoundedUp && (
            <p className="mt-1 text-[10px] leading-snug text-muted-foreground">
              {t("goldShop.pack.roundedUp")}
            </p>
          )}
          {bonusGoldOnPurchase > 0 && (
            <p className="mt-1 text-[11px] leading-snug text-gold">
              {t("goldShop.pack.fairnessTrigger", { gold: formatGold(pack.gold + bonusGoldOnPurchase) })}
            </p>
          )}
        </div>

        <Button className="w-full" variant="gold" onClick={onBuy} disabled={loading}>
          {loading
            ? t("goldShop.pack.opening")
            : triggersCycleReset
              ? t("goldShop.pack.claimBoost")
              : isDiscounted
                ? t("goldShop.pack.buyUpgrade")
                : t("goldShop.pack.buy")}
        </Button>
      </Panel>
    </motion.div>
  );
}
